import React, { Fragment, useCallback, useEffect, useState } from 'react';
import Module from '../../../../layout/Module';
import FilterButtonDevelopmentalRubric from '../Filter/Filter';
import { useSelector } from 'react-redux';
import { Alert } from '@mui/material';
import { getDevelopmentalRubricsGroupedByLearningContinuum } from '../../../../../services/developmental-rubrics/developmental-rubrics.service';
import { useParams } from 'react-router-dom';
import { handlerErrorV2 } from '../../../../../utils/errors';
import { columnsLearningContinuum } from '../../utils';
import { renderElementsTableLC } from '../../utils.jsx';
import DataGrid from '../../../../table/DataGrid';
import TextFormatDateRange from '../../../../text/TextFormatDateRange';
import TextEllipsis from '../../../../text/TextEllipsis';
import Loading from '../../../../Loading/Loading';

const GroupedByLearningContinuum = () => {
  const { curriculumId, studentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [groupBy, setGroupBy] = useState(null);
  const [error, setError] = useState(null);
  const state = useSelector((state) => state?.filter?.drGroupedByLearningContinuum);
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {});
  const years = useSelector((state) => state?.tenant?.years || []);
  const subjects = useSelector((state) => state?.subject?.list || []);

  useEffect(() => {
    const columns = columnsLearningContinuum(currentCurriculum, data);
    const groupBy = columns.find((col) => col.type === 'structure')?.key || columns[1]?.key || null;
    setGroupBy(groupBy);
    columns.push({
      label: 'Developmental Rubrics',
      key: 'dr',
      align: 'left',
      width: 800,
      show: true,
      hide: true
    });

    setColumns(columns.filter((column) => column.key !== groupBy && column.key !== 'version'));
  }, [currentCurriculum, data]);

  const onLoad = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDevelopmentalRubricsGroupedByLearningContinuum(curriculumId, {
        ...state.filter,
        studentId
      });
      if (!response?.data && response?.error) {
        setError(response.error.message || 'An error ocurred');
      } else {
        setData(response?.data || []);
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [curriculumId, state.filter]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <FilterButtonDevelopmentalRubric type={'drGroupedByLearningContinuum'} />
      </Module.BodyHeader>
      <Module.BodyContainer>
        {error ? (
          <Alert severity='error'>{error}</Alert>
        ) : loading ? (
          <Loading />
        ) : (
          <DataGrid
            id='developmental-rubrics-grouped-by-learning-continuum'
            loading={loading}
            groupBy={groupBy}
            rowsPerPage={data?.length}
            totalRows={data?.length}
            rows={data?.map((row, jndex) => {
              const elements = renderElementsTableLC(row.lc, currentCurriculum, 'lc');
              return {
                _id: `lc-${row.lc._id}-index-${jndex}`,
                ...row,
                ...elements,
                dr: (
                  <div key={`lc-${row.lc._id}-drs`} className='flex flex-col min-w-300px'>
                    {row.drs.map((dr, index) => {
                      const subject = subjects.find((subject) => subject._id === dr.subjectId);
                      const yearLevel = years
                        .filter((year) => dr.yearLevel.includes(year._id))
                        .map((year) => year.name)
                        .join(', ');
                      return (
                        <Fragment key={`lc-${row.lc._id}-dr-${dr.drId}-index-${index}-index-${index}`}>
                          <div className='border-l border-muted px-2 py-1'>
                            <div className='flex wrap items-center gap-2'>
                              <TextFormatDateRange
                                colorText={
                                  dr?.linkedClasses?.length > 0 ? 'text-accent' : 'text-primary opacity-50'
                                }
                                className='text-sm text-nowrap'
                                startDate={dr.startDate || ''}
                                endDate={dr.endDate || ''}
                              />

                              <span className='text-xs text-nowrap text-muted-foregroud opacity-75'>
                                {yearLevel}
                              </span>
                              {subject && (
                                <div className='max-w-100'>
                                  <TextEllipsis className='text-xs'>{subject.name}</TextEllipsis>
                                </div>
                              )}
                            </div>
                            <span>{dr.name}</span>
                          </div>
                          {index < row.drs.length - 1 && <div className='border-b border-muted'></div>}
                        </Fragment>
                      );
                    })}
                  </div>
                )
              };
            })}
            showSetting={false}
            columns={columns}
            labelNoRows="You don't have any developmental rubrics yet"
          />
        )}
      </Module.BodyContainer>
    </>
  );
};

export default GroupedByLearningContinuum;
